import PropTypes from 'prop-types';
import React, { useState, useCallback } from 'react';
import Popup from 'reactjs-popup';
import { Alert } from '@opendate-app/setlist';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import HoldChallengeRowAction from './HoldChallengeRowAction';
import { startedChallengeDescription } from '../api/holdChallengesApi';
import '../styles/holdReorderPopover.css';

const popupContentStyle = {
  border: 'none',
  borderRadius: '8px',
  boxShadow: '0 0 1px 1px rgba(0,0,0,0.02), 0 4px 6px 1px rgba(0,0,0,0.06)',
  padding: '0px',
  background: 'var(--od-surface, #fff)',
  width: '280px',
  maxHeight: '70vh',
  overflowY: 'auto',
};

const StartedChallengeAlert = ({ challenge }) => (
  <div className="hold-reorder-started-challenge">
    <Alert
      color="success"
      size="full-width"
      title="Challenge started"
      description={startedChallengeDescription(challenge)}
    />
  </div>
);

StartedChallengeAlert.propTypes = {
  challenge: PropTypes.shape({
    respond_by: PropTypes.string.isRequired,
    challenger: PropTypes.shape({ position: PropTypes.number, title: PropTypes.string }).isRequired,
    challenged: PropTypes.shape({ position: PropTypes.number, title: PropTypes.string }).isRequired
  }).isRequired
};

const HoldItem = ({ hold, positionLabel, hexColor, listeners, action }) => (
  <div className="hold-reorder-item d-flex align-items-center bg-white">
    <div
      {...listeners}
      className={`hold-reorder-grip setlist-text-xs d-flex align-items-center${listeners ? ' hold-reorder-grip--draggable' : ''}`}
    >
      <i className="far fa-grip-dots-vertical"></i>
    </div>
    {positionLabel && (
      <div
        className="hold-reorder-position-badge setlist-text-xs setlist-font-bold flex-shrink-0 text-center"
        style={{ borderColor: hexColor || 'var(--od-c-999999, #999)' }}
      >
        {positionLabel}
      </div>
    )}
    <span className="text-truncate setlist-text-xs">
      {hold.title}
    </span>
    {action}
  </div>
);

const SortableHold = ({ hold, positionLabel, hexColor, action }) => {
  const sortableId = 'hold-' + hold.hold_position_id;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: sortableId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <HoldItem
        hold={hold}
        positionLabel={positionLabel}
        hexColor={hexColor}
        listeners={listeners}
        action={action}
      />
    </div>
  );
};

const HoldReorderPopover = ({
  isOpen,
  onClose,
  holds,
  rooms,
  defaultTopHold,
  onReorder,
  date,
  team,
  canManageHoldChallenges,
  onChallengesChanged,
  startedChallenge,
}) => {
  const [holdOrder, setHoldOrder] = useState([]);
  const [positions, setPositions] = useState([]);
  const [initialHoldOrder, setInitialHoldOrder] = useState([]);
  const [initialPositions, setInitialPositions] = useState([]);
  const [activeHold, setActiveHold] = useState(null);
  const [fillToTop, setFillToTop] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const handleOpen = useCallback(() => {
    const sorted = [...holds].sort((a, b) => a.position - b.position);
    const pos = sorted.map((h) => h.position);
    setHoldOrder([...sorted]);
    setInitialHoldOrder([...sorted]);
    setPositions(pos);
    setInitialPositions(pos);
    setActiveHold(null);
    setFillToTop(false);
  }, [holds]);

  const handleDragStart = useCallback((event) => {
    const holdId = String(event.active.id).replace('hold-', '');
    setHoldOrder((prev) => {
      const found = prev.find((h) => String(h.hold_position_id) === holdId);
      if (found) setActiveHold(found);
      return prev;
    });
  }, []);

  const handleDragOver = useCallback((event) => {
    const { active, over } = event;
    if (!active || !over || active.id === over.id) return;

    const activeId = String(active.id);
    const overId = String(over.id);

    setHoldOrder((prev) => {
      const oldIndex = prev.findIndex((h) => 'hold-' + h.hold_position_id === activeId);
      const newIndex = prev.findIndex((h) => 'hold-' + h.hold_position_id === overId);
      if (oldIndex === -1 || newIndex === -1) return prev;
      if (oldIndex === newIndex) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
  }, []);

  const handleDragEnd = useCallback(() => {
    setActiveHold(null);
  }, []);

  const handleSave = useCallback((e) => {
    e.preventDefault();
    if (positions.some((p) => p < 1)) return;
    const positionMapping = {};
    holdOrder.forEach((hold, i) => {
      positionMapping[hold.hold_position_id] = positions[i];
    });
    onReorder(positionMapping);
    onClose();
  }, [holdOrder, positions, onReorder, onClose]);

  const effectiveTopHold = fillToTop ? 1 : (defaultTopHold || 1);

  const handleFillGaps = useCallback(() => {
    const newPositions = holdOrder.map((_, i) => effectiveTopHold + i);
    setPositions(newPositions);
  }, [holdOrder, effectiveTopHold]);

  const hasGaps = (() => {
    if (positions.length === 0) return false;
    if (positions[0] !== effectiveTopHold) return true;
    for (let i = 1; i < positions.length; i++) {
      if (positions[i] !== positions[i - 1] + 1) return true;
    }
    return false;
  })();

  const hasChanges = (() => {
    if (holdOrder.length !== initialHoldOrder.length) return true;
    const orderChanged = holdOrder.some((hold, i) => {
      return hold.hold_position_id !== initialHoldOrder[i].hold_position_id;
    });
    if (orderChanged) return true;
    return positions.some((pos, i) => pos !== initialPositions[i]);
  })();

  const getHexColor = (hold) => {
    const room = rooms.find((r) => r.id === hold.room_id);
    return room ? room.calendar_color_hex : '#999';
  };

  const sortableIds = holdOrder.map((h) => 'hold-' + h.hold_position_id);

  return (
    <Popup
      open={isOpen}
      onOpen={handleOpen}
      onClose={onClose}
      contentStyle={startedChallenge ? { ...popupContentStyle, width: '360px' } : popupContentStyle}
      overlayStyle={{ background: 'rgba(0,0,0,0.15)' }}
      modal
      closeOnDocumentClick={false}
    >
      <div className="hold-reorder-header">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <span className="setlist-text-sm setlist-font-semibold">
              Holds on this date
            </span>
            {date && (
              <div className="hold-reorder-date setlist-text-xs">
                {new Date(date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="hold-reorder-close-btn"
          >
            <i className="fas fa-times"></i>
          </button>
        </div>
        <div className="d-flex align-items-center" style={{ gap: 12, paddingTop: 4 }}>
          {hasGaps && (
            <button
              type="button"
              onClick={handleFillGaps}
              className="hold-reorder-fill-gaps-btn setlist-text-xs"
            >
              <i className="fas fa-arrow-up"></i>
              Fill gaps
            </button>
          )}
          {defaultTopHold > 1 && (
            <div className="hold-reorder-toggle d-flex align-items-center setlist-text-xs">
              <span
                className={`hold-reorder-toggle-option${!fillToTop ? ' hold-reorder-toggle-option--active' : ''}`}
                onClick={() => setFillToTop(false)}
              >
                Up to H{defaultTopHold}
              </span>
              <span
                className={`hold-reorder-toggle-option${fillToTop ? ' hold-reorder-toggle-option--active' : ''}`}
                onClick={() => setFillToTop(true)}
              >
                Up to H1
              </span>
            </div>
          )}
        </div>
      </div>
      {startedChallenge?.challenged && <StartedChallengeAlert challenge={startedChallenge} />}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={sortableIds}
          strategy={verticalListSortingStrategy}
        >
          {holdOrder.map((hold, index) => (
            <SortableHold
              key={'hold-' + hold.hold_position_id}
              hold={hold}
              positionLabel={'H' + positions[index]}
              hexColor={getHexColor(hold)}
              action={canManageHoldChallenges ? (
                <HoldChallengeRowAction
                  hold={hold}
                  isTopHold={initialHoldOrder[0]?.hold_position_id === hold.hold_position_id}
                  team={team}
                  onChallengesChanged={() => {
                    onChallengesChanged();
                    onClose();
                  }}
                />
              ) : null}
            />
          ))}
        </SortableContext>
        <DragOverlay dropAnimation={null}>
          {activeHold ? (
            <div className="hold-reorder-drag-overlay">
              <HoldItem hold={activeHold} />
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
      <div className="hold-reorder-footer d-flex">
        <button
          type="button"
          onClick={onClose}
          className="btn btn-setlist-secondary"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={!hasChanges}
          className="btn btn-setlist-primary"
        >
          Save Order
        </button>
      </div>
    </Popup>
  );
};

HoldReorderPopover.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  holds: PropTypes.array.isRequired,
  rooms: PropTypes.array.isRequired,
  defaultTopHold: PropTypes.number,
  onReorder: PropTypes.func.isRequired,
  date: PropTypes.string,
  team: PropTypes.object.isRequired,
  canManageHoldChallenges: PropTypes.bool.isRequired,
  onChallengesChanged: PropTypes.func.isRequired,
  startedChallenge: PropTypes.object,
};

export default HoldReorderPopover;
